import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, toDisplayString as _toDisplayString, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { computed, ref } from "vue";
import Layout from "@/layouts/workspace/Layout.vue";
import { useToast } from "primevue/usetoast";
import { useI18n } from "vue-i18n";
import { activeActivity } from "@/plugins/i18n";
import { featureIcons } from "@/layouts/workspace/menu-settings";
import ExpirationDialog from "@/components/common/ExpirationDialog.vue";
import { useTerminals } from "@/graphql/terminal/terminals";
import { useWHoAmI } from "@/graphql/user/whoAmi";
import { useRouter } from "vue-router";
import { EAccessMenu, Terminal, User } from "@/graphql/types";
import { CONSTANTS } from "@/graphql/utils/utils";
import { canNavigateAnywhere, getAccess, isAdmin } from "@/utils/activity";


export default /*#__PURE__*/_defineComponent({
  __name: 'WorkspaceLayout',
  setup(__props) {

const { terminals, terminalsLoading } = useTerminals();
const { Iam } = useWHoAmI();
const toast = useToast();
const { t } = useI18n();
const visible = ref(false);
const { push } = useRouter();

function needToConnect() {
  if (activeActivity.value.id === 0) {
    toast.add({
      severity: "error",
      summary: t("workspaceLayout.info"),
      detail: t(`workspaceLayout.connect`),
      life: parseInt(process.env.VUE_APP_TOAST_LIFE),
    });
    return true;
  }
  return false;
}

function navigateToPos(terminal: Terminal) {
  if (terminal.activityId === activeActivity.value.id) {
    localStorage.setItem(CONSTANTS.activeTerminal, JSON.stringify(terminal));
    void push({ name: "pos" }); // GO TO POS
  } else if (terminalsLoading.value) {
    toast.add({
      severity: "info",
      summary: t("workspaceLayout.terminalsLoading"),
      detail: t("workspaceLayout.waitTerminals"),
      life: parseInt(process.env.VUE_APP_TOAST_LIFE),
    });
  }
}

function isMyTerminal(users: User[]) {
  const canReadTerminal = activeActivity.value.access.find(
    (acc) => acc.menu === EAccessMenu.Terminal
  )?.read;
  if (canReadTerminal) return true;
  return users.findIndex((nu) => nu.id === Iam.value.id) > -1;
}

const terminalsMenu = computed(() => {
  return (
    canNavigateAnywhere()
      ? terminals.value
      : terminals.value.filter((ter) => isMyTerminal(ter.users))
  ).map((terminal) => ({
    label: terminal.name,
    description: terminal.description,
    disabled: !terminal.active,
    id: terminal.id,
    command: () => navigateToPos(terminal),
  }));
});

const workspaceMenu = computed(() => {
  const isForbidden = (route: EAccessMenu) => {
    if (canNavigateAnywhere()) return "";
    const canRead = getAccess(route)?.read;
    return canRead ? "" : "display: none";
  };

  return [
    { label: "activity", to: "/workspace/activity" },
    { separator: !isForbidden(EAccessMenu.Activity) },
    {
      label: "category",
      to: "/workspace/category",
      style: isForbidden(EAccessMenu.Category),
      command: () => needToConnect(),
    },
    { separator: !isForbidden(EAccessMenu.Product) },
    {
      label: "product",
      to: "/workspace/product",
      style: isForbidden(EAccessMenu.Product),
      command: () => needToConnect(),
    },
    { separator: !isForbidden(EAccessMenu.Stock) },
    {
      label: "stock",
      to: "/workspace/stock",
      style: isForbidden(EAccessMenu.Stock),
      command: () => needToConnect(),
    },
    { separator: !isForbidden(EAccessMenu.Customer) },
    {
      label: "customer",
      to: "/workspace/customer",
      style: isForbidden(EAccessMenu.Customer),
      command: () => needToConnect(),
    },
    { separator: !isForbidden(EAccessMenu.Pos) },
    {
      label: "pos",
      command: () => onPosClick(),
      style: isForbidden(EAccessMenu.Pos),
    },
    { separator: !isForbidden(EAccessMenu.Expense) },
    {
      label: "expense",
      to: "/workspace/expense",
      command: () => needToConnect(),
      style: isForbidden(EAccessMenu.Expense),
    },
    { separator: !isForbidden(EAccessMenu.Rapport) },
    {
      label: "rapport",
      to: "/workspace/rapport",
      style: isForbidden(EAccessMenu.Rapport),
      command: () => needToConnect(),
    },
    { separator: !isForbidden(EAccessMenu.Rh) },
    {
      label: "hr",
      to: "/workspace/hr",
      style: isForbidden(EAccessMenu.Rh),
      command: () => needToConnect(),
    },
  ];
});

function onPosClick() {
  if (needToConnect()) return;
  switch (terminalsMenu.value.length) {
    case 0:
      toast.add({
        severity: "error",
        summary: t("terminal.access"),
        detail: t("terminal.notFound"),
        life: parseInt(process.env.VUE_APP_TOAST_LIFE),
      });
      break;
    case 1:
      if (terminalsMenu.value[0].disabled) {
        toast.add({
          severity: "error",
          summary: t("terminal.access"),
          detail: t("terminal.noAccess"),
          life: parseInt(process.env.VUE_APP_TOAST_LIFE),
        });
      } else
        navigateToPos(
          terminals.value.find((t) => t.id === terminalsMenu.value[0].id)
        );
      break;
    default:
      visible.value = true;
      break;
  }
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!
  const _component_Button = _resolveComponent("Button")!
  const _component_Menu = _resolveComponent("Menu")!
  const _component_Dialog = _resolveComponent("Dialog")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(Layout, {
      menu: workspaceMenu.value,
      activity: _unref(activeActivity).name
    }, {
      default: _withCtx(() => [
        (_unref(isAdmin)())
          ? (_openBlock(), _createBlock(_component_router_link, {
              key: 0,
              style: {"font-size":"20px"},
              to: "/admin"
            }, {
              default: _withCtx(() => [
                _createElementVNode("i", {
                  class: _normalizeClass(_unref(featureIcons).administration)
                }, null, 2),
                _createTextVNode(" Administration ")
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["menu", "activity"]),
    _createVNode(ExpirationDialog),
    _createVNode(_component_Dialog, {
      modal: "",
      visible: visible.value,
      "onUpdate:visible": _cache[0] || (_cache[0] = ($event: any) => ((visible).value = $event)),
      header: _ctx.$t('terminal.choose')
    }, {
      header: _withCtx(() => []),
      footer: _withCtx(() => []),
      default: _withCtx(() => [
        _createVNode(_component_Menu, {
          class: "p-pt-2",
          model: terminalsMenu.value,
          style: { width: '100%' }
        }, {
          item: _withCtx(({ item }) => [
            _createVNode(_component_Button, {
              disabled: !!item.disabled,
              onClick: item.command,
              style: {"width":"100%"},
              class: "p-button-text p-d-block p-button-secondary p-p-0 p-mb-2"
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", null, _toDisplayString(item.label), 1),
                _createElementVNode("small", null, _toDisplayString(item.description), 1)
              ]),
              _: 2
            }, 1032, ["disabled", "onClick"])
          ]),
          _: 1
        }, 8, ["model"])
      ]),
      _: 1
    }, 8, ["visible", "header"])
  ], 64))
}
}

})